import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { sellerService } from "../../services/enhancedCrudService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";

const PostsPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPostDetail, setLoadingPostDetail] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postDetail, setPostDetail] = useState(null);
  const [formData, setFormData] = useState({
    caption: "",
  });

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await sellerService.getPosts();
      setPosts(response.data || []);
    } catch (error) {
      console.error("Error fetching posts:", error);
      toast.error("Error fetching posts");
    } finally {
      setLoading(false);
    }
  };

  const updatePost = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await sellerService.updatePost(selectedPost.id, {
        caption: formData.caption,
      });
      toast.success("Post updated successfully!");
      setShowEditModal(false);
      setSelectedPost(null);
      setFormData({ caption: "" });
      fetchPosts();
    } catch (error) {
      console.error("Error updating post:", error);
      toast.error("Error updating post");
    } finally {
      setLoading(false);
    }
  };

  const deletePost = async (postId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this post?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        await sellerService.deletePost(postId);
        toast.success("Post deleted successfully!");
        fetchPosts();
      } catch (error) {
        console.error("Error deleting post:", error);
        toast.error("Error deleting post");
      } finally {
        setLoading(false);
      }
    }
  };

  const openEditModal = (post) => {
    setSelectedPost(post);
    setFormData({
      caption: post.caption || "",
    });
    setShowEditModal(true);
  };

  const openViewModal = async (post) => {
    setSelectedPost(post);
    setLoadingPostDetail(true);
    setShowViewModal(true);

    try {
      const response = await sellerService.getPostDetail(post.id);
      setPostDetail(response);
    } catch (error) {
      console.error("Error fetching post detail:", error);
      toast.error("Error loading post details");
      setPostDetail(null);
    } finally {
      setLoadingPostDetail(false);
    }
  };

  const resetForm = () => {
    setFormData({ caption: "" });
    setSelectedPost(null);
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title">Social Posts</h4>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/social/posts/create")}
                disabled={loading}
              >
                <i className="feather icon-plus"></i> Create Post
              </button>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center text-muted">
                  <i
                    className="feather icon-message-circle"
                    style={{ fontSize: "3rem" }}
                  ></i>
                  <p>No posts found. Create your first post!</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Sr.No</th>
                        <th>Thumbnail</th>
                        <th>Caption</th>
                        <th>Created At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {posts.map((post,index) => (
                        <tr key={index}>
                      <td>{index + 1}</td>
                          <td>
                            {post.thumbnail ? (
                              <img
                                src={post.thumbnail}
                                alt="Post Thumbnail"
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <span className="text-muted">No Image</span>
                            )}
                          </td>
                          <td>
                            <div
                              style={{
                                maxWidth: "300px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {post.caption}
                            </div>
                          </td>
                          <td>
                            {new Date(post.created_at).toLocaleDateString()}
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <i
                                className="pointer fas fa-eye text-info"
                                onClick={() => openViewModal(post)}
                                title="View Post"
                              ></i>
                              <i
                                className="pointer fas fa-edit text-primary"
                                onClick={() => openEditModal(post)}
                                title="Edit Post"
                              ></i>
                              <i
                                className="pointer fas fa-trash text-danger"
                                onClick={() => deletePost(post.id)}
                                title="Delete Post"
                              ></i>{" "}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showEditModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Post</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={updatePost}>
                <div className="modal-body">
                  <div className="form-group">
                    <label htmlFor="editCaption">Caption</label>
                    <textarea
                      id="editCaption"
                      className="form-control"
                      rows="3"
                      value={formData.caption}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          caption: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={() => {
                      setShowEditModal(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? "Updating..." : "Update Post"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {showEditModal && <div className="modal-backdrop fade show"></div>}
      {showViewModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div
              className="modal-content"
              style={{ border: "none", borderRadius: "8px" }}
            >
              <div
                className="modal-header"
                style={{ borderBottom: "none", padding: "16px 20px 0" }}
              >
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedPost(null);
                    setPostDetail(null);
                  }}
                  style={{
                    position: "absolute",
                    right: "20px",
                    top: "16px",
                    zIndex: 1,
                  }}
                >
                  <span style={{ fontSize: "24px", color: "#65676b" }}>
                    &times;
                  </span>
                </button>
              </div>
              <div className="modal-body" style={{ padding: "0 20px 20px" }}>
                {loadingPostDetail ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "40px",
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                    }}
                  >
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                ) : (
                  selectedPost && (
                    <div
                      style={{
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          padding: "12px 16px",
                          borderBottom: "1px solid #dadde1",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: "#1877f2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "16px",
                            marginRight: "12px",
                          }}
                        >
                          M
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontWeight: "600",
                              fontSize: "15px",
                              color: "#1c1e21",
                              lineHeight: "1.33",
                            }}
                          >
                            Morbi Bazar
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#65676b",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <span>
                              {new Date(
                                selectedPost.created_at
                              ).toLocaleDateString("en-US", {
                                month: "long",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </span>
                            <span style={{ margin: "0 4px" }}>·</span>
                            <i
                              className="fas fa-globe"
                              style={{ fontSize: "12px" }}
                            ></i>
                          </div>
                        </div>
                      </div>
                      <div style={{ padding: "12px 16px" }}>
                        <div
                          style={{
                            fontSize: "15px",
                            lineHeight: "1.33",
                            color: "#1c1e21",
                            marginBottom:
                              postDetail?.media || selectedPost.thumbnail
                                ? "12px"
                                : "0",
                          }}
                        >
                          {postDetail?.caption ||
                            selectedPost.caption ||
                            "No caption available"}
                        </div>
                      </div>
                      {(postDetail?.media || selectedPost.thumbnail) && (
                        <div
                          style={{
                            width: "100%",
                            backgroundColor: "#f0f2f5",
                          }}
                        >
                          {(() => {
                            const mediaUrl = postDetail?.media;
                            const isVideo = postDetail?.media_type === "video";
                            const isImage = postDetail?.media_type === "image";
                            console.log(postDetail);
                            if (isVideo) {
                              return (
                                <div
                                  style={{
                                    width: "100%",
                                    aspectRatio: "1",
                                    backgroundColor: "#f0f2f5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    overflow: "hidden",
                                  }}
                                >
                                  <video
                                    controls
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  >
                                    <source src={mediaUrl} type="video/mp4" />
                                    Your browser does not support the video tag.
                                  </video>
                                </div>
                              );
                            } else if (isImage) {
                              return (
                                <div
                                  style={{
                                    width: "100%",
                                    aspectRatio: "1",
                                    backgroundColor: "#f0f2f5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    overflow: "hidden",
                                  }}
                                >
                                  <img
                                    src={mediaUrl}
                                    alt="Post Media"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                </div>
                              );
                            } else {
                              return (
                                <div
                                  style={{
                                    padding: "40px",
                                    textAlign: "center",
                                    color: "#65676b",
                                  }}
                                >
                                  <i
                                    className="fas fa-file"
                                    style={{
                                      fontSize: "2rem",
                                      marginBottom: "10px",
                                    }}
                                  ></i>
                                  <p>Media file</p>
                                  <a
                                    href={mediaUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#1877f2",
                                      textDecoration: "none",
                                    }}
                                  >
                                    View File
                                  </a>
                                </div>
                              );
                            }
                          })()}
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showViewModal && <div className="modal-backdrop fade show"></div>}
    </div>
  );
};

export default PostsPage;
